import type { ItemFieldRow } from './item-field-reader';
import { phpJsonTruthy } from './php-json-values';

function arrayLike(value: unknown): value is ItemFieldRow { return value !== null && typeof value === 'object'; }
function clone(value: unknown): unknown {
    if (!arrayLike(value)) return value ?? null;
    const entries = Object.entries(value);
    return entries.every(([key], index) => key === String(index))
        ? entries.map(([, child]) => clone(child))
        : Object.fromEntries(entries.map(([key, child]) => [key, clone(child)]));
}

/** Request-owned _ft_resolve_inventory_instance_full memo. The callback supplies
 * get_inventory_arr's complete result; this module performs no fetch or compile.
 * Exact ID spelling is the cache key, including PHP check_uuid's final-LF case.
 * Failed/re-entrant builds retain the null seed, including after a callback throws.
 * A caller missing required inputs must abort that compile, not reuse its memo as
 * evidence of absence. Omit the callback only to model PHP's unloaded helper.
 */
export function createInventoryInstanceResolver(getInventory?: (id: string) => unknown): (id: string | null) => ItemFieldRow | null {
    const memo = new Map<string, ItemFieldRow | null>();
    return id => {
        if (id === null || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?:\n)?(?![\s\S])/i.test(id)) return null;
        if (memo.has(id)) return clone(memo.get(id)) as ItemFieldRow | null;
        memo.set(id, null);
        if (!getInventory) return null;
        const full = getInventory(id);
        const accepted = arrayLike(full) && Object.prototype.hasOwnProperty.call(full, 'item_layer_types')
            && phpJsonTruthy((full as Readonly<Record<string, unknown>>).item_layer_types);
        const stored = accepted ? clone(full) as ItemFieldRow : null;
        memo.set(id, stored);
        return clone(stored) as ItemFieldRow | null;
    };
}
